import bootBlock, { documentSignal } from '../../src/js/block-boot';

function initDocCenter(root) {
  const block         = root.querySelector('.doc-center');
  const list          = block?.querySelector('#doc-center-list');
  if (!list || list.dataset.runtimeReady === 'true') return;
  list.dataset.runtimeReady = 'true';

  const ownerDoc      = block.ownerDocument;
  const signal        = documentSignal(ownerDoc, 'documentation-center');
  const loading       = block.querySelector('#doc-center-loading');
  const empty         = block.querySelector('#doc-center-empty');
  const moreBtn       = block.querySelector('#doc-center-more');
  const searchEl      = block.querySelector('#doc-center-search');
  const activeBar     = block.querySelector('#doc-center-active-filters');
  const tagsEl        = block.querySelector('#doc-center-tags');
  const clearAllBtn   = block.querySelector('#doc-center-clear-all');
  const dropdowns     = block.querySelectorAll('.doc-center__dropdown');

  const { restUrl, perPage = 20 } = window.docCenterData || {};

  let page       = 1;
  let fetching   = false;
  let controller = null;
  let generation = 0;

  // ── Mobile filter sheet ────────────────────────────────────────────────

  const filterToggle  = block.querySelector('#doc-center-filter-toggle');
  const filtersPanel  = block.querySelector('#doc-center-filters');
  const filterClose   = block.querySelector('#doc-center-filter-close');
  const overlay       = block.querySelector('#doc-center-overlay');

  const openFilters = () => {
    filterToggle?.setAttribute('aria-expanded', 'true');
    filtersPanel?.classList.add('is-open');
    overlay?.classList.add('is-open');
    ownerDoc.body.style.overflow = 'hidden';
  };

  const closeFilters = () => {
    filterToggle?.setAttribute('aria-expanded', 'false');
    filtersPanel?.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    ownerDoc.body.style.overflow = '';
  };

  filterToggle?.addEventListener('click', () => {
    filterToggle.getAttribute('aria-expanded') === 'true' ? closeFilters() : openFilters();
  });
  filterClose?.addEventListener('click', closeFilters);
  overlay?.addEventListener('click', closeFilters);

  // ── Dropdown toggle ────────────────────────────────────────────────────

  const closeAll = () => {
    dropdowns.forEach(dd => {
      dd.querySelector('.doc-center__dropdown-btn')?.setAttribute('aria-expanded', 'false');
      dd.querySelector('.doc-center__dropdown-panel')?.setAttribute('hidden', '');
    });
  };

  dropdowns.forEach(dd => {
    const btn   = dd.querySelector('.doc-center__dropdown-btn');
    const panel = dd.querySelector('.doc-center__dropdown-panel');

    btn?.addEventListener('click', () => {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      closeAll();
      if (!isOpen) {
        btn.setAttribute('aria-expanded', 'true');
        panel?.removeAttribute('hidden');
      }
    });

    panel?.querySelectorAll('.doc-center__checkbox').forEach(cb => {
      cb.addEventListener('change', () => {
        updateDropdownLabel(dd);
        updateActiveTags();
        reset();
      });
    });
  });

  ownerDoc.addEventListener('click', e => {
    if (!e.target.closest('.doc-center__dropdown')) closeAll();
  }, { signal });
  ownerDoc.addEventListener('keydown', e => {
    if (e.key === 'Escape') closeAll();
  }, { signal });

  // ── Dropdown label (e.g. "All product lines (2)") ──────────────────────

  const updateDropdownLabel = dd => {
    const label       = dd.querySelector('.doc-center__dropdown-label');
    const defaultText = dd.dataset.defaultLabel || '';
    const checked     = dd.querySelectorAll('.doc-center__checkbox:checked');
    if (label) {
      label.textContent = checked.length
        ? `${defaultText} (${checked.length})`
        : defaultText;
    }
  };

  // ── Active filters tags ────────────────────────────────────────────────

  const updateActiveTags = () => {
    if (!tagsEl || !activeBar) return;
    tagsEl.innerHTML = '';

    let total = 0;
    dropdowns.forEach(dd => {
      const filter = dd.dataset.filter;
      dd.querySelectorAll('.doc-center__checkbox:checked').forEach(cb => {
        total++;
        const li  = ownerDoc.createElement('li');
        const tag = ownerDoc.createElement('button');
        tag.type      = 'button';
        tag.className = 'doc-center__tag';
        tag.appendChild(ownerDoc.createTextNode(cb.dataset.label || cb.value));
        const x = ownerDoc.createElement('span');
        x.setAttribute('aria-hidden', 'true');
        x.textContent = '×';
        tag.appendChild(x);
        tag.addEventListener('click', () => {
          cb.checked = false;
          updateDropdownLabel(dd);
          updateActiveTags();
          reset();
        });
        li.appendChild(tag);
        tagsEl.appendChild(li);
      });
    });

    activeBar.hidden = total === 0;
  };

  clearAllBtn?.addEventListener('click', () => {
    dropdowns.forEach(dd => {
      dd.querySelectorAll('.doc-center__checkbox:checked').forEach(cb => { cb.checked = false; });
      updateDropdownLabel(dd);
    });
    updateActiveTags();
    reset();
  });

  // ── Build query params ─────────────────────────────────────────────────

  const getFilters = () => {
    const params = new URLSearchParams({ page, per_page: perPage });
    const q = searchEl?.value.trim();
    if (q) params.set('s', q);

    dropdowns.forEach(dd => {
      const filter  = dd.dataset.filter;
      const checked = [...dd.querySelectorAll('.doc-center__checkbox:checked')];
      checked.forEach(cb => params.append(`${filter}[]`, cb.value));
    });

    return params;
  };

  // ── Render rows ────────────────────────────────────────────────────────

  const iconSvg = (path, title) =>
    `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" role="img" aria-label="${title}">
      <title>${title}</title>
      <path stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="${path}"/>
    </svg>`;

  const eyePath      = 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z';
  const downloadPath = 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3';

  const safeUrl = url => {
    try {
      const { protocol } = new URL(url);
      return protocol === 'http:' || protocol === 'https:' ? url : '';
    } catch {
      return '';
    }
  };

  const renderItem = doc => {
    const li = ownerDoc.createElement('li');
    li.className = 'doc-center__row';

    const nameSpan = ownerDoc.createElement('span');
    nameSpan.className = 'doc-center__doc-name';
    nameSpan.textContent = doc.title || '';

    const lineSpan = ownerDoc.createElement('span');
    lineSpan.className = 'doc-center__line-cell';
    if (doc.product_line?.name) {
      const iconSrc = safeUrl(doc.product_line.icon_url || '');
      if (iconSrc) {
        const img = ownerDoc.createElement('img');
        img.src = iconSrc;
        img.alt = '';
        img.className = 'doc-center__line-icon';
        img.width = 20;
        img.height = 20;
        img.loading = 'lazy';
        lineSpan.appendChild(img);
      }
      const nameEl = ownerDoc.createElement('span');
      nameEl.textContent = doc.product_line.name;
      lineSpan.appendChild(nameEl);
    } else {
      lineSpan.textContent = '—';
    }

    const typeSpan = ownerDoc.createElement('span');
    typeSpan.className = 'doc-center__type-cell';
    typeSpan.textContent = doc.document_type || '—';

    const actionsSpan = ownerDoc.createElement('span');
    actionsSpan.className = 'doc-center__actions';

    const fileUrl = safeUrl(doc.file_url || '');

    const viewLink = ownerDoc.createElement('a');
    if (fileUrl) viewLink.href = fileUrl;
    viewLink.target = '_blank';
    viewLink.rel = 'noopener noreferrer';
    viewLink.className = 'doc-center__action';
    viewLink.innerHTML = iconSvg(eyePath, 'View');
    actionsSpan.appendChild(viewLink);

    if (!doc.is_external && fileUrl) {
      const dlLink = ownerDoc.createElement('a');
      dlLink.href = fileUrl;
      dlLink.download = '';
      dlLink.className = 'doc-center__action';
      dlLink.innerHTML = iconSvg(downloadPath, 'Download');
      actionsSpan.appendChild(dlLink);
    }

    li.appendChild(nameSpan);
    li.appendChild(lineSpan);
    li.appendChild(typeSpan);
    li.appendChild(actionsSpan);

    return li;
  };

  // ── Fetch ──────────────────────────────────────────────────────────────

  const setLoading = state => {
    fetching = state;
    if (loading) loading.hidden = !state;
    if (moreBtn) moreBtn.disabled = state;
  };

  const fetch_ = async (append = false) => {
    generation++;
    const thisGen = generation;

    controller?.abort();
    controller = new AbortController();

    setLoading(true);
    if (empty) empty.hidden = true;

    try {
      const res  = await fetch(`${restUrl}?${getFilters()}`, { signal: controller.signal });
      const data = await res.json();

      if (thisGen !== generation) return;

      if (!append) list.innerHTML = '';

      if (!data.items?.length && !append) {
        if (empty) empty.hidden = false;
      } else {
        const frag = ownerDoc.createDocumentFragment();
        (data.items || []).forEach(doc => frag.appendChild(renderItem(doc)));
        list.appendChild(frag);
      }

      if (moreBtn) moreBtn.hidden = !data.has_more;
    } catch (e) {
      if (e.name !== 'AbortError') console.error('[doc-center]', e);
    } finally {
      if (thisGen === generation) setLoading(false);
    }
  };

  const reset = () => { page = 1; fetch_(false); };

  let debounceTimer;
  searchEl?.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reset, 300);
  });

  moreBtn?.addEventListener('click', () => { page++; fetch_(true); });

  fetch_(false);
}

bootBlock('documentation-center', initDocCenter);
